import { isEqual } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import uuid from 'react-uuid';
import { api } from 'src/assets/static/ApiRoutes';
import { ButtonState } from 'src/components/design-system/Button';
import useAsync from 'src/hooks/useAsync';
import {
  InvoiceLineItemError,
  InvoiceLineItemInfo,
  InvoiceLineItemWarning,
} from 'src/sections/invoice_details/errors/useInvoiceLineItemErrors';
import axiosInstance from 'src/utils/axios';
import { GlCodesObject } from './useGlCodesApi';

export interface InvoiceQuantity {
  glCode: string;
  price: number;
  quantity: number;
  glPeriods: number[];
}

export interface InvoiceLineItem {
  invoiceNumber?: string;
  receiptLineItemId: number;
  quantityReceived: number;
  priceAmt: number;
  isTaxable: boolean;
  canDelete: ButtonState;
  completed: string;
  glCd: string;
  productItemNum: string;
  description: string;
  uom: string;
  previousQty: number;
  subTotal: number;
  rowId: string;
  varianceQty?: number;
  glCodeDescription: string;
  isNewLineItem?: boolean;
  isCombinedLineItem?: boolean;
  poNum?: string;
  poLineItemNumber: number;
  poQty: number;
  poGlCd: string;
  poPrice: number;
  poSubtotal: number;
  poUom: string;
  poGlCodeDescription: string;
  receivedQty: number;
  invoicedQuantities: InvoiceQuantity[];
  origInvoiceQty: null | number;
  isNewlyAddedLineItemNow: boolean;
}

export interface InvoiceLineItemWithWarnings extends InvoiceLineItem {
  errors?: InvoiceLineItemError;
  warnings?: InvoiceLineItemWarning;
  informations?: InvoiceLineItemInfo;
  hasErrorsOrWarnings: boolean;
  selected?: boolean;
  expanded: boolean;
}

const useInvoiceLineItemsApi = (
  invoiceId: string,
  invoiceNumber?: string | number,
  poNum?: string,
  glCodesObject?: GlCodesObject,
  isGlCodesRequired?: boolean
) => {
  const [initialInvoiceLineItems, setInitialInvoiceLineItems] = useState([] as InvoiceLineItem[]);

  const call = useCallback(async () => {
    try {
      if (isGlCodesRequired && (!glCodesObject || !Object.keys(glCodesObject).length)) {
        return [];
      }
      const res = await axiosInstance.get<InvoiceLineItem[]>(api.getInvoiceLineItems(invoiceId));
      const lineItems = res?.data?.map((lineItem) => ({
        ...lineItem,
        invoiceNumber,
        poNum,
        rowId: uuid(),
        completed: !(lineItem.poQty > lineItem.previousQty + lineItem.quantityReceived) ? 'Y' : 'N',
        isTaxable: !!lineItem?.isTaxable,
        expanded: false,
        selected: false,
        glCodeDescription: glCodesObject?.[lineItem?.glCd]?.label,
        poGlCodeDescription: glCodesObject?.[lineItem?.poGlCd]?.label,
        hasErrorsOrWarnings: false,
      })) as InvoiceLineItem[];
      setInitialInvoiceLineItems(lineItems);
      return lineItems;
    } catch (error) {
      throw new Error(error);
    }
  }, [isGlCodesRequired, glCodesObject, invoiceId, invoiceNumber, poNum]);

  const { execute, isLoading, value, setValue } = useAsync(call, true, undefined, []);

  const isInvoiceLineItemsDirty = useMemo(
    () => !isEqual(initialInvoiceLineItems, value),
    [initialInvoiceLineItems, value]
  );

  return {
    isInvoiceLineItemsLoading: isLoading,
    invoiceLineItems: value,
    setInvoiceLineItems: setValue,
    getInvoiceLineItems: execute,
    isInvoiceLineItemsDirty,
    initialInvoiceLineItems,
  };
};

export default useInvoiceLineItemsApi;
